import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { LoadMore } from '@/ui/LoadMore/LoadMore';
import { getBackgroundColorStyle } from '@/utils/background';
import { computeFont } from '@/utils/fonts';

import { RatingDistribution } from './components/RatingDistribution';
import { RatingSummary } from './components/RatingSummary';
import { ReviewCard } from './components/ReviewCard';

import {
  DEFAULT_PROGRESS_BAR_BG_COLOR,
  DEFAULT_PROGRESS_BAR_COLOR,
  DEFAULT_SECONDARY_TEXT_COLOR,
  DEFAULT_STAR_COLOR,
  DEFAULT_TEXT_COLOR,
} from './constants';
import type { ProductReviewsElementProps } from './ProductReviewsElement.types';

export function ProductReviewsElement(props: ProductReviewsElementProps) {
  const {
    element,
    children,
    attributes,
    id,
    isLive = false,
    onLoadMoreReviews,
    isLoadingMoreReviews = false,
    translations,
    viewport,
  } = props;

  const {
    data,
    gap = '24px',
    padding,
    backgroundColor,
    mobileGap,
    mobileReviewsFeedGap,
    mobileReviewsFeedTitleFontSize,
    mobileReviewsFeedAuthorFontSize,
    mobileReviewsFeedTextFontSize,

    hideRatingSummary = false,
    hideRatingDistribution = false,
    hideReviewsList = false,

    ratingSummaryStarColor = DEFAULT_STAR_COLOR,
    ratingSummaryEmptyStarColor = DEFAULT_PROGRESS_BAR_BG_COLOR,
    ratingSummaryTextColor = DEFAULT_TEXT_COLOR,
    ratingSummaryCountColor = DEFAULT_SECONDARY_TEXT_COLOR,
    ratingSummaryFontSize = '16px',
    ratingSummaryFontFamily,
    ratingSummaryFontWeight = '510',

    ratingDistributionBarColor = DEFAULT_PROGRESS_BAR_COLOR,
    ratingDistributionBarBackgroundColor = DEFAULT_PROGRESS_BAR_BG_COLOR,
    ratingDistributionTextColor = DEFAULT_TEXT_COLOR,
    ratingDistributionFontSize = '16px',
    ratingDistributionFontFamily,

    reviewsFeedTitleText = 'Reviews',
    reviewsFeedTitleColor = DEFAULT_TEXT_COLOR,
    reviewsFeedTitleFontSize = '16px',
    reviewsFeedTitleFontFamily,
    reviewsFeedTitleFontWeight = '510',
    reviewsFeedAuthorColor = DEFAULT_TEXT_COLOR,
    reviewsFeedAuthorFontSize = '14px',
    reviewsFeedAuthorFontFamily,
    reviewsFeedAuthorFontWeight = '510',
    reviewsFeedTextColor = DEFAULT_SECONDARY_TEXT_COLOR,
    reviewsFeedTextFontSize = '14px',
    reviewsFeedTextFontFamily,
    showReviewDate = false,
    reviewsFeedDateColor = DEFAULT_SECONDARY_TEXT_COLOR,
    reviewsFeedDateFontSize = '12px',
    reviewsFeedDateFontFamily,
    reviewsFeedStarColor = DEFAULT_STAR_COLOR,
    reviewsFeedEmptyStarColor = DEFAULT_PROGRESS_BAR_BG_COLOR,
    reviewsFeedGap = '24px',

    reviewsFeedPaginateButtonText = 'Load more',
    reviewsFeedPaginateButtonBusyText = 'Loading...',
    reviewsFeedPaginateButtonBackgroundColor = DEFAULT_STAR_COLOR,
    reviewsFeedPaginateButtonTextColor = 'var(--text-on-accent)',
    reviewsFeedPaginateButtonFontFamily,
    reviewsFeedPaginateButtonFontSize = '16px',
    reviewsFeedPaginateButtonFontWeight = '500',
    reviewsFeedPaginateButtonBorderRadius = '8px',
    reviewsFeedPaginateButtonPadding = '12px 16px',
    reviewsFeedPaginationMarginTop = '16px',
  } = element.attrs;

  const { reviews = [], summary } = data || {};

  const titleText =
    reviewsFeedTitleText === 'Reviews' ? (translations?.['product_reviews.title'] ?? 'Reviews') : reviewsFeedTitleText;
  const loadMoreText =
    reviewsFeedPaginateButtonText === 'Load more'
      ? (translations?.['product_reviews.load_more'] ?? 'Load more')
      : reviewsFeedPaginateButtonText;
  const loadingText =
    reviewsFeedPaginateButtonBusyText === 'Loading...'
      ? (translations?.['product_reviews.loading'] ?? 'Loading...')
      : reviewsFeedPaginateButtonBusyText;

  const pagination = data?.pagination;
  const hasMoreReviews = pagination ? pagination.page < pagination.total_pages : false;
  const showLoadMore = !hideReviewsList && (props.showLoadMore ?? (hasMoreReviews || !isLive));

  const isMobile = viewport === 'mobile';
  const effectiveGap = (isMobile && mobileGap) || gap;
  const effectiveReviewsFeedGap = (isMobile && mobileReviewsFeedGap) || reviewsFeedGap;
  const effectiveTitleFontSize = (isMobile && mobileReviewsFeedTitleFontSize) || reviewsFeedTitleFontSize;
  const effectiveAuthorFontSize = (isMobile && mobileReviewsFeedAuthorFontSize) || reviewsFeedAuthorFontSize;
  const effectiveTextFontSize = (isMobile && mobileReviewsFeedTextFontSize) || reviewsFeedTextFontSize;

  return (
    <div
      id={id}
      className={classNames('relative flex flex-col w-full', !isLive && 'pointer-events-none')}
      style={
        {
          gap: effectiveGap,
          padding,
          ...getBackgroundColorStyle(backgroundColor),
          '--product-reviews-star-color': ratingSummaryStarColor,
          '--product-reviews-empty-star-color': ratingSummaryEmptyStarColor,
          '--product-reviews-summary-text-color': ratingSummaryTextColor,
          '--product-reviews-summary-count-color': ratingSummaryCountColor,
          '--product-reviews-summary-font-size': ratingSummaryFontSize,
          '--product-reviews-summary-font-family': computeFont(ratingSummaryFontFamily),
          '--product-reviews-summary-font-weight': ratingSummaryFontWeight,
          '--product-reviews-distribution-bar-color': ratingDistributionBarColor,
          '--product-reviews-distribution-bar-bg-color': ratingDistributionBarBackgroundColor,
          '--product-reviews-distribution-text-color': ratingDistributionTextColor,
          '--product-reviews-distribution-font-size': ratingDistributionFontSize,
          '--product-reviews-distribution-font-family': computeFont(ratingDistributionFontFamily),
          '--product-reviews-feed-title-color': reviewsFeedTitleColor,
          '--product-reviews-feed-title-font-size': effectiveTitleFontSize,
          '--product-reviews-feed-title-font-family': computeFont(reviewsFeedTitleFontFamily),
          '--product-reviews-feed-title-font-weight': reviewsFeedTitleFontWeight,
          '--product-reviews-feed-author-color': reviewsFeedAuthorColor,
          '--product-reviews-feed-author-font-size': effectiveAuthorFontSize,
          '--product-reviews-feed-author-font-family': computeFont(reviewsFeedAuthorFontFamily),
          '--product-reviews-feed-author-font-weight': reviewsFeedAuthorFontWeight,
          '--product-reviews-feed-text-color': reviewsFeedTextColor,
          '--product-reviews-feed-text-font-size': effectiveTextFontSize,
          '--product-reviews-feed-text-font-family': computeFont(reviewsFeedTextFontFamily),
          '--product-reviews-feed-star-color': reviewsFeedStarColor,
          '--product-reviews-feed-empty-star-color': reviewsFeedEmptyStarColor,
          '--product-reviews-feed-date-color': reviewsFeedDateColor,
          '--product-reviews-feed-date-font-size': reviewsFeedDateFontSize,
          '--product-reviews-feed-date-font-family': computeFont(reviewsFeedDateFontFamily),
          '--pagination-margin-top': reviewsFeedPaginationMarginTop,
        } as CSSProperties
      }
      {...attributes}
    >
      {summary && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            {(!hideRatingDistribution || !hideReviewsList) && (
              <h3
                style={{
                  margin: 0,
                  textAlign: 'left',
                  fontWeight: 'var(--product-reviews-feed-title-font-weight)',
                  fontSize: 'var(--product-reviews-feed-title-font-size)',
                  fontFamily: 'var(--product-reviews-feed-title-font-family)',
                  lineHeight: '24px',
                  color: 'var(--product-reviews-feed-title-color)',
                }}
              >
                {titleText}
              </h3>
            )}
            {!hideRatingSummary && <RatingSummary summary={summary} translations={translations} />}
          </div>
          {!hideRatingDistribution && <RatingDistribution summary={summary} />}
        </div>
      )}

      {!hideReviewsList && reviews.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: effectiveReviewsFeedGap }}>
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} showDate={showReviewDate} />
          ))}
        </div>
      )}

      {showLoadMore && (
        <LoadMore
          onClick={() => onLoadMoreReviews?.()}
          isBusy={isLoadingMoreReviews}
          isLive={isLive}
          attributes={{
            paginateButtonText: loadMoreText,
            paginateButtonBusyText: loadingText,
            paginateButtonBackgroundColor: reviewsFeedPaginateButtonBackgroundColor,
            paginateButtonTextColor: reviewsFeedPaginateButtonTextColor,
            paginateButtonFontFamily: computeFont(reviewsFeedPaginateButtonFontFamily),
            paginateButtonFontSize: reviewsFeedPaginateButtonFontSize,
            paginateButtonFontWeight: reviewsFeedPaginateButtonFontWeight,
            paginateButtonBorderRadius: reviewsFeedPaginateButtonBorderRadius,
            paginateButtonPadding: reviewsFeedPaginateButtonPadding,
            paginationMarginTop: reviewsFeedPaginationMarginTop,
          }}
        />
      )}

      {children}
    </div>
  );
}
